import {useAnalytics} from '@github-ui/use-analytics'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {FINDINGS_ANALYTICS_EVENTS} from './analytics'
import {expandedFindingsStorageKey} from './storage-keys'

const normalizeExpandedFindingIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []

  const ids = new Set<string>()
  for (const item of value) {
    if (typeof item === 'string' && item.length > 0) {
      ids.add(item)
    }
  }
  return [...ids]
}

export const useExpandedFindings = (pullRequestBasePath: string) => {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const [expandedFindingIds, setExpandedFindingIds] = useSessionStorage<string[]>(
    expandedFindingsStorageKey(pullRequestBasePath),
    [],
  )
  const expandedFindingIdSet = useMemo(
    () => new Set(normalizeExpandedFindingIds(expandedFindingIds)),
    [expandedFindingIds],
  )
  // Track the latest expanded set in a ref so the toggle callback can detect
  // expand-vs-collapse without depending on the set (which would change the
  // callback identity on every toggle and re-render every finding row).
  const expandedFindingIdSetRef = useRef(expandedFindingIdSet)
  useEffect(() => {
    expandedFindingIdSetRef.current = expandedFindingIdSet
  }, [expandedFindingIdSet])
  const handleToggleFindingExpanded = useCallback(
    (findingId: string) => {
      const willExpand = !expandedFindingIdSetRef.current.has(findingId)
      setExpandedFindingIds(prev => {
        const next = new Set(normalizeExpandedFindingIds(prev))
        if (next.has(findingId)) next.delete(findingId)
        else next.add(findingId)
        return [...next]
      })
      if (willExpand) {
        sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.expand.type, FINDINGS_ANALYTICS_EVENTS.expand.target)
      }
    },
    [setExpandedFindingIds, sendAnalyticsEvent],
  )
  const handleRemoveExpandedFindings = useCallback(
    (findingIds: string[]) => {
      setExpandedFindingIds(prev => {
        const next = new Set(normalizeExpandedFindingIds(prev))
        for (const id of findingIds) next.delete(id)
        return [...next]
      })
    },
    [setExpandedFindingIds],
  )

  return {expandedFindingIdSet, handleToggleFindingExpanded, handleRemoveExpandedFindings}
}
